import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';

import { get, put } from 'api/requests';

import { getUpdatedData } from 'features/ApartmentList/utils/getUpdatedData';
import { getPreparedFurnishingTabs } from '../helpers';

import { AppThunk } from 'store/types';
import { IParamsReq } from 'types';
import {
  ApartmentsList,
  ApartmentsListStore,
  ISavedMedia,
  IOptionsGroups,
  IOption,
  IOptionsMedia,
} from 'features/ApartmentList/types';

const initialState: ApartmentsListStore = {
  loading: false,
  list: [],
  mediaData: [],
  loadingMedia: false,
  tabs: [],
};

const reset: CaseReducer<ApartmentsListStore> = () => initialState;

const loading: CaseReducer<ApartmentsListStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

const loadingMedia: CaseReducer<ApartmentsListStore, PayloadAction<boolean>> = (state, action) => {
  state.loadingMedia = action.payload;
};

const list: CaseReducer<ApartmentsListStore, PayloadAction<ApartmentsList[]>> = (state, action) => {
  state.list = action.payload;
};

const mediaData: CaseReducer<ApartmentsListStore, PayloadAction<IOptionsMedia[]>> = (
  state,
  action,
) => {
  state.mediaData = action.payload;
};

const tabs: CaseReducer<ApartmentsListStore, PayloadAction<IOption[]>> = (state, action) => {
  state.tabs = action.payload;
};

export const apartmentListSlice = createSlice({
  name: 'apartmentList',
  initialState,
  reducers: {
    reset,
    loading,
    list,
    loadingMedia,
    tabs,
    mediaData,
  },
});

const {
  reset: setReset,
  loading: setLoading,
  list: setApartmentsList,
  loadingMedia: setLoadingMedia,
  mediaData: setMediaData,
  tabs: setTabs,
} = apartmentListSlice.actions;

export const getApartmentsList =
  (search: IParamsReq): AppThunk =>
  dispatch => {
    const { structure, section } = search;
    dispatch(setLoading(true));

    get<ApartmentFloorsResponce>(
      `/api/standards/structures/${structure}/sections/${section}/placements/partner/`,
      search as AxiosRequestConfig['params'],
    )
      .then(({ data }) => {
        const { floors } = data;
        dispatch(setApartmentsList(floors));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const putApartmentMedia =
  (placement: number, media: ISavedMedia[], callback?: () => void): AppThunk =>
  (dispatch, getState) => {
    const {
      apartmentList: { list },
    } = getState();

    dispatch(setLoadingMedia(true));

    put<IOptionsMedia[]>(`/api/standards/placements/${placement}/edit-furnishing-media/`, media)
      .then(({ data }) => {
        const media = data;
        const updatedData = getUpdatedData(list, placement, media);
        dispatch(setApartmentsList(updatedData));
      })
      .finally(() => {
        dispatch(setLoadingMedia(false));
        callback && callback();
      });
  };

export const getMedia =
  (placementId: number): AppThunk =>
  dispatch => {
    dispatch(setLoadingMedia(true));

    get<IOptionsMedia[]>(`/api/v2/standards/placements/${placementId}/options-media/`)
      .then(({ data }) => {
        dispatch(setMediaData(data));
      })
      .finally(() => dispatch(setLoadingMedia(false)));
  };

export const getTabs =
  (cardId: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<IOptionsGroups>(`/api/standards/placements/${cardId}/options-groups/`)
      .then(({ data }) => {
        const preparedTabs = getPreparedFurnishingTabs(data);
        dispatch(setTabs(preparedTabs));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export { setMediaData, setReset };

export type ApartmentFloorsResponce = {
  floors: ApartmentsList[];
};
